import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Container } from '../../components/Container'
import { CircularProgress, Typography, useTheme } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import Plot from 'react-plotly.js'
import { KPIController } from '../../hooks/useKPI'
import { TurnProjectsTable } from './TurnProjectsTable'
import {
    DEFAULT_CAI_PASS_THRESHOLD,
    DEFAULT_SORT_DIRECTION,
    DEFAULT_SORT_FIELD,
    DEFAULT_TABLE_FILTERS,
    METHOD_COLORS,
    METHOD_LABELS,
    METHOD_ORDER,
    SortDirection,
    SortField,
    TableFilters,
    YEAR_GROUP_GAP,
} from './turnProjectsConstants'

type ViewMode = 'chart' | 'table'

type ChartBar = {
    x: number
    label: string
    methodKey: string
    year: string | number
    pct: number
    count: number
    total: number
    caiAvg: number
    caiPooled: number
    caiPassRate: number
    caiPassThreshold: number
}

export const TurnProjects = ({
    KPIController,
}: {
    KPIController: KPIController
}) => {
    const theme = useTheme()
    const [viewMode, setViewMode] = useState<ViewMode>('chart')

    const [tableFilters, setTableFilters] = useState<TableFilters>(
        DEFAULT_TABLE_FILTERS,
    )
    const [showSourceBreakdown, setShowSourceBreakdown] = useState(false)
    const [sortField, setSortField] = useState<SortField>(DEFAULT_SORT_FIELD)
    const [sortDirection, setSortDirection] = useState<SortDirection>(
        DEFAULT_SORT_DIRECTION,
    )
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    useEffect(() => {
        KPIController.getTurnProjects()
    }, [])

    const chart = KPIController.turnProjectsChart
    const detail = KPIController.turnProjectsDetail
    const loading = !chart || !detail

    const caiPassThreshold =
        chart?.cai_pass_threshold ?? DEFAULT_CAI_PASS_THRESHOLD

    const yearOptions = useMemo(() => {
        const years = new Set<number>()
        detail?.forEach((row) => {
            if (row.year != null) {
                years.add(row.year)
            }
        })
        chart?.years.forEach((year) => years.add(year))
        return Array.from(years).sort()
    }, [detail, chart])

    const handleDrillDown = useCallback(
        (methodKey: string, year: string | number) => {
            setTableFilters({
                ...DEFAULT_TABLE_FILTERS,
                primaryMethod: methodKey,
                year: typeof year === 'number' ? year : Number(year),
            })
            setSortField(DEFAULT_SORT_FIELD)
            setSortDirection(DEFAULT_SORT_DIRECTION)
            setPage(0)
            setViewMode('table')
        },
        [],
    )

    const handleSortChange = useCallback(
        (field: SortField) => {
            if (sortField === field) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            } else {
                setSortField(field)
                setSortDirection(field === 'cai_rate' ? 'desc' : 'asc')
            }
            setPage(0)
        },
        [sortField],
    )

    const { chartBars, yearAnnotations } = useMemo(() => {
        const bars: ChartBar[] = []
        const annotations: Partial<Plotly.Annotations>[] = []

        if (!chart) {
            return { chartBars: bars, yearAnnotations: annotations }
        }

        const yearTotals = chart.years.map((_, yearIndex) =>
            METHOD_ORDER.reduce(
                (sum, key) => sum + (chart.series[key]?.[yearIndex] ?? 0),
                0,
            ),
        )

        let xPos = 0
        chart.years.forEach((year, yearIndex) => {
            const yearStart = xPos
            const yearBars = METHOD_ORDER.filter(
                (key) => chart.series[key],
            ).map((key) => {
                const count = chart.series[key]?.[yearIndex] ?? 0
                const total = yearTotals[yearIndex]
                const pct = total > 0 ? (count / total) * 100 : 0
                const caiAvg = chart.cai_avg?.[key]?.[yearIndex] ?? 0
                const caiPooled = chart.cai_pooled?.[key]?.[yearIndex] ?? 0
                const caiPassRate = chart.cai_pass_rate?.[key]?.[yearIndex] ?? 0
                return {
                    label: METHOD_LABELS[key] ?? key,
                    methodKey: key,
                    year,
                    pct,
                    count,
                    total,
                    caiAvg,
                    caiPooled,
                    caiPassRate,
                    caiPassThreshold:
                        chart.cai_pass_threshold ?? DEFAULT_CAI_PASS_THRESHOLD,
                }
            })
            yearBars.sort((a, b) => b.pct - a.pct)

            yearBars.forEach((bar) => {
                bars.push({
                    x: xPos,
                    ...bar,
                })
                xPos += 1
            })

            if (yearBars.length > 0) {
                const yearCenter = (yearStart + xPos - 1) / 2
                annotations.push({
                    x: yearCenter,
                    xref: 'x',
                    y: 1.04,
                    yref: 'paper',
                    text: `<b>${year}</b>`,
                    showarrow: false,
                    font: { size: 13 },
                })
            }
            xPos += YEAR_GROUP_GAP
        })

        return { chartBars: bars, yearAnnotations: annotations }
    }, [chart])

    const handlePlotClick = useCallback(
        (event: Readonly<Plotly.PlotMouseEvent>) => {
            const point = event.points?.[0]
            if (point == null || point.pointNumber == null) {
                return
            }
            const bar = chartBars[point.pointNumber]
            if (!bar || bar.count === 0) {
                return
            }
            handleDrillDown(bar.methodKey, bar.year)
        },
        [chartBars, handleDrillDown],
    )

    const chartTrace = useMemo(
        () =>
            chartBars.length > 0
                ? {
                      type: 'bar' as const,
                      name: '% of Projects',
                      x: chartBars.map((bar) => bar.x),
                      y: chartBars.map((bar) => bar.pct),
                      customdata: chartBars.map((bar) => [
                          bar.count,
                          bar.total,
                          bar.year,
                          bar.label,
                          bar.caiAvg,
                          bar.caiPooled,
                      ]),
                      hovertemplate:
                          '%{customdata[3]} (%{customdata[2]})<br>' +
                          'Adoption: %{y:.1f}% (%{customdata[0]} of %{customdata[1]})<br>' +
                          'Avg CAI rate: %{customdata[4]:.1f}%<br>' +
                          'Pooled CAI rate: %{customdata[5]:.1f}%<extra></extra>',
                      text: chartBars.map((bar) => `${bar.pct.toFixed(1)}%`),
                      textposition: 'outside' as const,
                      textfont: { size: 11 },
                      cliponaxis: false,
                      marker: {
                          color: chartBars.map(
                              (bar) =>
                                  METHOD_COLORS[bar.methodKey] ??
                                  theme.palette.primary.main,
                          ),
                      },
                  }
                : null,
        [chartBars, theme.palette.primary.main],
    )

    const caiDotTrace = useMemo(
        () =>
            chartBars.length > 0
                ? {
                      type: 'scatter' as const,
                      mode: 'markers' as const,
                      name: `Schedules ≥${
                          chartBars[0]?.caiPassThreshold ?? 70
                      }% CAI`,
                      x: chartBars.map((bar) => bar.x),
                      y: chartBars.map((bar) =>
                          bar.count > 0 ? bar.caiPassRate : null,
                      ),
                      customdata: chartBars.map((bar) => [
                          bar.label,
                          bar.year,
                          bar.count,
                          bar.caiAvg,
                          bar.caiPooled,
                          bar.caiPassThreshold,
                      ]),
                      hovertemplate:
                          '%{customdata[0]} (%{customdata[1]})<br>' +
                          '≥%{customdata[5]:.0f}% CAI: %{y:.1f}% (%{customdata[2]} projects)<br>' +
                          'Avg CAI rate: %{customdata[3]:.1f}%<br>' +
                          'Pooled CAI rate: %{customdata[4]:.1f}%<extra></extra>',
                      marker: {
                          color: theme.palette.common.black,
                          size: 11,
                          line: { color: theme.palette.common.white, width: 2 },
                      },
                  }
                : null,
        [chartBars, theme.palette.common.black, theme.palette.common.white],
    )

    const plotData = useMemo(
        () =>
            chartTrace
                ? [chartTrace, ...(caiDotTrace ? [caiDotTrace] : [])]
                : [],
        [chartTrace, caiDotTrace],
    )

    const plotLayout = useMemo(
        () => ({
            autosize: true,
            title: {
                text: 'Turn Projects by Primary Scheduling Method',
            },
            xaxis: {
                tickmode: 'array' as const,
                tickvals: chartBars.map((bar) => bar.x),
                ticktext: chartBars.map((bar) => bar.label),
                tickangle: -45,
                automargin: true,
            },
            yaxis: {
                title: { text: '%' },
                ticksuffix: '%',
                range: [0, 100],
            },
            annotations: yearAnnotations,
            showlegend: true,
            legend: {
                orientation: 'h' as const,
                yanchor: 'bottom' as const,
                y: 1.02,
                xanchor: 'right' as const,
                x: 1,
            },
            margin: { b: 120, t: 80 },
        }),
        [chartBars, yearAnnotations],
    )

    const plotConfig = useMemo(() => ({ displayModeBar: false }), [])
    const plotStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                padding: theme.spacing(2, 3),
                boxSizing: 'border-box',
            }}
        >
            <Container
                style={{
                    marginBottom: theme.spacing(2),
                    justifyContent: 'flex-end',
                }}
            >
                <ToggleButtonGroup
                    exclusive
                    value={viewMode}
                    onChange={(_, value: ViewMode | null) => {
                        if (value) {
                            setViewMode(value)
                        }
                    }}
                    size="small"
                >
                    <ToggleButton value="chart">Chart</ToggleButton>
                    <ToggleButton value="table">Table</ToggleButton>
                </ToggleButtonGroup>
            </Container>

            {loading ? (
                <Container style={{ flex: 1, justifyContent: 'center' }}>
                    <CircularProgress />
                </Container>
            ) : viewMode === 'chart' ? (
                <Container
                    className="turn-projects-plot-container"
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    {chartTrace ? (
                        <Plot
                            data={plotData}
                            layout={plotLayout}
                            config={plotConfig}
                            useResizeHandler
                            style={plotStyle}
                            onClick={handlePlotClick}
                        />
                    ) : (
                        <Typography>No turn project data available.</Typography>
                    )}
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    <TurnProjectsTable
                        rows={detail}
                        caiPassThreshold={caiPassThreshold}
                        filters={tableFilters}
                        onFiltersChange={setTableFilters}
                        showSourceBreakdown={showSourceBreakdown}
                        onShowSourceBreakdownChange={setShowSourceBreakdown}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={setRowsPerPage}
                        yearOptions={yearOptions}
                    />
                </Container>
            )}
        </Container>
    )
}
